import React, { FormEvent, useState } from 'react'

import { theme } from '@acorns/grove'
import { TextareaInput } from '@acorns/web-components'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import {
  AiChatWidgetSupportEmailDrawerButtonTappedAction,
  trackAiChatWidgetSupportEmailDrawerButtonTappedPayload,
  trackAiChatWidgetSupportEmailDrawerViewedPayload,
} from 'generated/trackingEvents'
import {
  useCreateAiChatSupportTicketMutation,
  useUserQueryQuery,
} from 'generated/types'
import useAnalyticsComponentViewed from 'hooks/useAnalyticsComponentViewed'

import { StyledButton, StyledDrawer } from './styled-components'
import { SuccessDrawer } from './SuccessDrawer'

const StyledTextareaInput = styled(TextareaInput)`
  width: 100%;
`

const Form = styled.form`
  margin-top: 20px;
`

const EmailDrawer = ({
  isEmailDrawerOpen,
  setIsEmailDrawerOpen,
}: {
  isEmailDrawerOpen: boolean
  setIsEmailDrawerOpen: (isEmailDrawerOpen: boolean) => void
}) => {
  const { getRemoteString } = useRemoteStrings()
  const { track } = useAnalyticsClient()
  const [contactReason, setContactReason] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccessAnimationCompleted, setIsSuccessAnimationCompleted] =
    useState(false)

  const { data: userData } = useUserQueryQuery()
  const userInfo = userData?.user || undefined

  const [createSupportTicket] = useCreateAiChatSupportTicketMutation({
    onCompleted: () => {
      setShowSuccess(true)
    },
    onError: () => {
      setIsSubmitting(false)
    },
  })

  const emailDrawerRef = useAnalyticsComponentViewed({
    generatedPayload: trackAiChatWidgetSupportEmailDrawerViewedPayload(),
  })

  const handleClose = () => {
    setContactReason('')
    setMessage('')
    setIsEmailDrawerOpen(false)
    setIsSubmitting(false)
    setShowSuccess(false)
    setIsSuccessAnimationCompleted(false)
  }

  const handleAnimationEnd = () => {
    setIsSuccessAnimationCompleted(true)
  }

  const handleSetContactReason = (event: FormEvent<HTMLTextAreaElement>) => {
    setContactReason(event.currentTarget.value)
  }

  const handleSetMessage = (event: FormEvent<HTMLTextAreaElement>) => {
    setMessage(event.currentTarget.value)
  }

  const handleCreateSupportTicket = () => {
    createSupportTicket({
      variables: {
        input: {
          category: contactReason || 'Other',
          email: userInfo?.email || '',
          firstName: userInfo?.firstName || '',
          lastName: userInfo?.lastName || '',
          message: message || '',
          phone: userInfo?.phoneNumber || '',
        },
      },
    })

    track(
      ...trackAiChatWidgetSupportEmailDrawerButtonTappedPayload({
        action: AiChatWidgetSupportEmailDrawerButtonTappedAction.Submit,
        ctaTitle: 'Submit',
      }),
    )

    setIsSubmitting(true)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleCreateSupportTicket()
  }

  const handleValidateInputs = () => {
    return contactReason.trim() !== '' && message.trim() !== ''
  }

  if (isSubmitting) {
    return (
      <SuccessDrawer
        isOpen={isEmailDrawerOpen}
        showSuccess={showSuccess}
        isSuccessAnimationCompleted={isSuccessAnimationCompleted}
        handleAnimationEnd={handleAnimationEnd}
        handleClose={handleClose}
        messages={{
          successTitle: getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.success.title',
          }),
          successBody: getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.success.body',
          }),
          submittingMessage: getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.submission.title',
          }),
        }}
      />
    )
  }

  return (
    <StyledDrawer
      isOpen={isEmailDrawerOpen}
      onClose={handleClose}
      title={getRemoteString({
        key: 'acorns_intelligence.chat.email_support_drawer.title',
      })}
      customBreakpoint={theme.dimensions.breakpointsSmall}
    >
      <Form
        aria-label="Email support form"
        onSubmit={handleSubmit}
        ref={emailDrawerRef}
      >
        <StyledTextareaInput
          id="ai-chat-support-reason"
          label={getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.form.reason.label',
          })}
          name="ai-chat-support-reason"
          onChange={handleSetContactReason}
          placeholder={getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.form.reason.hint',
          })}
          value={contactReason}
          maxlength={250}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <StyledTextareaInput
          id="ai-chat-support-message"
          label={getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.form.label',
          })}
          name="ai-chat-support-message"
          onChange={handleSetMessage}
          placeholder={getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.form.hint',
          })}
          value={message}
          maxlength={250}
        />
        <StyledButton
          onPress={handleCreateSupportTicket}
          disabled={!handleValidateInputs()}
          buttonType="submit"
        >
          {getRemoteString({
            key: 'acorns_intelligence.chat.email_support_drawer.form.cta',
          })}
        </StyledButton>
      </Form>
    </StyledDrawer>
  )
}

export { EmailDrawer }
